import React from 'react';
import { Box, InputAdornment, TextField, Typography } from '@mui/material';

import { InitiativeDetailFinancialIcon, InitiativeDetailRealtimeIcon } from 'assets';
import { formatCurrency, formatLocaleNumber, formatNullable } from 'utils';
import { FieldLabel } from 'pages/authenticated/accelerate/initiatives-management/components';
import { SectionCard } from '../section-card';
import { detailInputSx } from '../../styles';
import { parseNum } from '../../helpers';
import { ReadOnlyPanel } from '../read-only-panel';
import { ReadOnlyStat } from '../read-only-stat';
import { FinancialSectionProps } from './types';
import { FINANCIAL_TOOLTIPS } from './consts';

export const FinancialSection: React.FC<FinancialSectionProps> = ({
    form,
    updateField,
    derived,
    initiative,
}) => (
    <SectionCard
        title="Financial Impact"
        icon={<InitiativeDetailFinancialIcon width={36} height={36} />}
        headerExtra={
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.5,
                    py: 0.75,
                    backgroundColor: '#F1FAE8',
                    border: '1px solid rgba(104, 172, 57, 0.2)',
                    borderRadius: '8px',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', '& path': { fill: '#68AC39' } }}>
                    <InitiativeDetailRealtimeIcon width={10} height={11} />
                </Box>
                <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: '#68AC39', whiteSpace: 'nowrap' }}>
                    Auto-Calculation Active
                </Typography>
            </Box>
        }
    >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 160px' }}>
                    <FieldLabel tooltip={FINANCIAL_TOOLTIPS.capEx}>CapEx</FieldLabel>
                    <TextField
                        fullWidth size="small" type="number"
                        placeholder="0"
                        value={form.capex ?? ''}
                        onChange={(e) => updateField('capex', parseNum(e.target.value))}
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        sx={detailInputSx}
                    />
                </Box>
                <Box sx={{ flex: '1 1 160px' }}>
                    <FieldLabel tooltip={FINANCIAL_TOOLTIPS.opexChange}>Annual OpEx Change (±)</FieldLabel>
                    <TextField
                        fullWidth size="small" type="number"
                        placeholder="0"
                        value={form.annualOpexChange ?? ''}
                        onChange={(e) => updateField('annualOpexChange', parseNum(e.target.value))}
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        sx={detailInputSx}
                    />
                </Box>
                <Box sx={{ flex: '1 1 160px' }}>
                    <FieldLabel tooltip={FINANCIAL_TOOLTIPS.grossSavings}>Annual Gross Savings</FieldLabel>
                    <TextField
                        fullWidth size="small" type="number"
                        placeholder="0"
                        value={form.annualGrossSavings ?? ''}
                        onChange={(e) => updateField('annualGrossSavings', parseNum(e.target.value))}
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        sx={detailInputSx}
                    />
                </Box>
                <Box sx={{ flex: '1 1 160px' }}>
                    <FieldLabel tooltip={FINANCIAL_TOOLTIPS.tco2eAvoided}>Annual tCO2e Avoided</FieldLabel>
                    <TextField
                        fullWidth size="small" type="number"
                        placeholder="0"
                        value={form.annualTco2eAvoided ?? ''}
                        onChange={(e) => updateField('annualTco2eAvoided', parseNum(e.target.value))}
                        InputProps={{ endAdornment: <InputAdornment position="end">tCO2e</InputAdornment> }}
                        sx={detailInputSx}
                    />
                </Box>
                <Box sx={{ flex: '1 1 140px' }}>
                    <FieldLabel tooltip={FINANCIAL_TOOLTIPS.lifespan}>Project Lifespan (years)</FieldLabel>
                    <TextField
                        fullWidth size="small" type="number"
                        placeholder={initiative.lifespan_is_default ? '10 (default)' : '10'}
                        value={form.projectLifespan ?? ''}
                        onChange={(e) => updateField('projectLifespan', parseNum(e.target.value))}
                        sx={detailInputSx}
                    />
                </Box>
                <Box sx={{ flex: '1 1 140px' }}>
                    <FieldLabel tooltip={FINANCIAL_TOOLTIPS.discountRate}>Discount Rate (%)</FieldLabel>
                    <TextField
                        fullWidth size="small" type="number"
                        placeholder={initiative.discount_rate_is_default ? '8 (default)' : '8'}
                        value={form.discountRate ?? ''}
                        onChange={(e) => updateField('discountRate', parseNum(e.target.value))}
                        sx={detailInputSx}
                    />
                </Box>
            </Box>

            <Box>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>
                    Projected Returns
                </Typography>
                <ReadOnlyPanel>
                    <ReadOnlyStat
                        label="Net Annual Benefit"
                        sublabel="Savings − OpEx"
                        value={formatNullable(derived?.nab ?? null, formatCurrency)}
                        tooltip={FINANCIAL_TOOLTIPS.netAnnualBenefit}
                    />
                    <ReadOnlyStat
                        label="Simple Payback"
                        sublabel="CapEx / Net Benefit"
                        value={formatNullable(derived?.simplePayback ?? null, (v) => `${formatLocaleNumber(v)} yrs`)}
                        tooltip={FINANCIAL_TOOLTIPS.simplePayback}
                    />
                </ReadOnlyPanel>
            </Box>
        </Box>
    </SectionCard>
);
